import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Badge, Grid, StatCard, PageHeader } from '../../ui/components';
import { DollarSign, Check, Clock, TrendingUp, TrendingDown, Calendar, Infinity } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';
import { supabase } from '../../lib/supabase';
import { AppLayout } from '../../components/layout';
import { REFERRAL_NAV } from '../../config/navigation';

const EarningsPage = () => {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState({
    totalEarnings: 0,
    paidEarnings: 0,
    pendingEarnings: 0,
    thisMonth: 0,
    lastMonth: 0,
    avgPerReferral: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const session = localStorage.getItem('lk_partner_session');
    if (!session) {
      navigate('/partner/login');
      return;
    }
    
    const partnerData = JSON.parse(session);
    setPartner(partnerData);
    fetchEarningsData(partnerData.id);
  }, [navigate]);

  const fetchEarningsData = async (partnerId) => {
    setLoading(true);
    try {
      const { data: referralsData } = await supabase
        .from('referrals')
        .select('*, loan_applications(id, status, loan_amount, created_at)')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false });

      if (referralsData) {
        setReferrals(referralsData);
        
        // Calculate earnings (example: $500 per funded loan)
        const fundedReferrals = referralsData.filter(r => r.status === 'funded');
        const approvedReferrals = referralsData.filter(r => r.status === 'approved');
        
        const totalEarnings = fundedReferrals.length * 500;
        const pendingEarnings = approvedReferrals.length * 500;
        
        // Calculate monthly earnings
        const now = new Date();
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        
        const thisMonthFunded = referralsData.filter(r => 
          r.status === 'funded' && new Date(r.updated_at || r.created_at) >= thisMonthStart
        ).length;
        
        const lastMonthFunded = referralsData.filter(r => 
          r.status === 'funded' && 
          new Date(r.updated_at || r.created_at) >= lastMonthStart &&
          new Date(r.updated_at || r.created_at) <= lastMonthEnd
        ).length;
        
        setEarnings({
          totalEarnings,
          paidEarnings: totalEarnings, // In real app, track payment status
          pendingEarnings,
          thisMonth: thisMonthFunded * 500,
          lastMonth: lastMonthFunded * 500,
          avgPerReferral: referralsData.length > 0 ? totalEarnings / referralsData.length : 0,
        });
      }
    } catch (error) {
      console.error('Error fetching earnings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('lk_partner_session');
    navigate('/partner/login');
  };

  if (!partner) return null;

  return (
    <AppLayout
      role="referral"
      navItems={REFERRAL_NAV}
      user={{
        name: `${partner.firstName} ${partner.lastName}`,
        email: partner.email,
        role: 'referral',
      }}
      onLogout={handleLogout}
    >
      <div>
        <PageHeader title="Earnings" description="Track your referral earnings and payments" />

        {/* Earnings Stats */}
        <Grid cols={3} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
          <Surface padding="var(--lk-space-4)" style={{ borderLeft: '3px solid var(--lk-success-500)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)' }}>
              <div style={{ width: 48, height: 48, borderRadius: 'var(--lk-radius-md)', background: 'var(--lk-success-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DollarSign size={24} color="var(--lk-success-500)" />
              </div>
              <div>
                <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>Total Earnings</div>
                <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700, color: 'var(--lk-success-600)' }}>${earnings.totalEarnings.toLocaleString()}</div>
                <div style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-xs)' }}>All time</div>
              </div>
            </div>
          </Surface>
          <Surface padding="var(--lk-space-4)" style={{ borderLeft: '3px solid var(--lk-primary-500)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)' }}>
              <div style={{ width: 48, height: 48, borderRadius: 'var(--lk-radius-md)', background: 'var(--lk-primary-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Check size={24} color="var(--lk-primary-500)" />
              </div>
              <div>
                <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>Paid Earnings</div>
                <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>${earnings.paidEarnings.toLocaleString()}</div>
                <div style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-xs)' }}>Received</div>
              </div>
            </div>
          </Surface>
          <Surface padding="var(--lk-space-4)" style={{ borderLeft: '3px solid var(--lk-warning-500)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)' }}>
              <div style={{ width: 48, height: 48, borderRadius: 'var(--lk-radius-md)', background: 'var(--lk-warning-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Clock size={24} color="var(--lk-warning-500)" />
              </div>
              <div>
                <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>Pending Earnings</div>
                <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700, color: 'var(--lk-warning-600)' }}>${earnings.pendingEarnings.toLocaleString()}</div>
                <div style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-xs)' }}>In progress</div>
              </div>
            </div>
          </Surface>
        </Grid>

        {/* Monthly Performance */}
        <Grid cols={3} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
          <Surface padding="var(--lk-space-4)">
            <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)', marginBottom: 'var(--lk-space-2)' }}>This Month</div>
            <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700 }}>${earnings.thisMonth.toLocaleString()}</div>
            {earnings.lastMonth > 0 && (
              <div style={{ marginTop: 'var(--lk-space-2)' }}>
                {earnings.thisMonth > earnings.lastMonth ? (
                  <Badge variant="success">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <TrendingUp size={12} />
                      {((earnings.thisMonth - earnings.lastMonth) / earnings.lastMonth * 100).toFixed(1)}% vs last month
                    </span>
                  </Badge>
                ) : earnings.thisMonth < earnings.lastMonth ? (
                  <Badge variant="danger">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <TrendingDown size={12} />
                      {((earnings.lastMonth - earnings.thisMonth) / earnings.lastMonth * 100).toFixed(1)}% vs last month
                    </span>
                  </Badge>
                ) : (
                  <Badge variant="neutral">Same as last month</Badge>
                )}
              </div>
            )}
          </Surface>
          <StatCard label="Last Month" value={`$${earnings.lastMonth.toLocaleString()}`} />
          <StatCard label="Avg Per Referral" value={`$${earnings.avgPerReferral.toFixed(0)}`} />
        </Grid>

        {/* Earnings Breakdown */}
        <Surface padding="0" style={{ marginBottom: 'var(--lk-space-4)', overflow: 'hidden' }}>
          <div style={{ padding: 'var(--lk-space-4) var(--lk-space-5)', borderBottom: '1px solid var(--lk-border-subtle)' }}>
            <h5 style={{ fontWeight: 700, margin: 0 }}>Earnings Breakdown</h5>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="lk-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Date Funded</th>
                  <th>Loan Amount</th>
                  <th>Commission</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: 'var(--lk-space-8)' }}>
                      <div className="lk-spinner lk-spinner-sm" style={{ display: 'inline-block', marginRight: 8 }} />
                      Loading earnings...
                    </td>
                  </tr>
                ) : referrals.filter(r => r.status === 'funded' || r.status === 'approved').length > 0 ? (
                  referrals
                    .filter(r => r.status === 'funded' || r.status === 'approved')
                    .map((referral) => (
                      <tr key={referral.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--lk-primary-100)', color: 'var(--lk-primary-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--lk-text-xs)', fontWeight: 600, flexShrink: 0 }}>
                              {referral.applicant_name?.[0] || referral.applicant_email?.[0] || '?'}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500 }}>{referral.applicant_name || 'Anonymous'}</div>
                              <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{referral.applicant_email || 'No email'}</div>
                            </div>
                          </div>
                        </td>
                        <td>{new Date(referral.updated_at || referral.created_at).toLocaleDateString()}</td>
                        <td style={{ fontWeight: 600 }}>
                          {referral.loan_applications?.loan_amount
                            ? `$${referral.loan_applications.loan_amount.toLocaleString()}`
                            : '-'}
                        </td>
                        <td style={{ fontWeight: 700, color: 'var(--lk-success-600)' }}>$500</td>
                        <td>
                          {referral.status === 'funded' ? (
                            <Badge variant="success">Paid</Badge>
                          ) : (
                            <Badge variant="warning">Pending</Badge>
                          )}
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)', padding: 'var(--lk-space-8)' }}>
                      No earnings yet. Keep referring to start earning!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Surface>

        {/* Commission Structure */}
        <Surface padding="var(--lk-space-5)">
          <h5 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-4)' }}>Commission Structure</h5>
          <Grid cols={3} gap="var(--lk-space-3)">
            <Surface padding="var(--lk-space-4)" style={{ textAlign: 'center' }}>
              <Check size={40} color="var(--lk-success-500)" style={{ marginBottom: 'var(--lk-space-2)' }} />
              <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700, color: 'var(--lk-success-600)' }}>$500</div>
              <div style={{ color: 'var(--lk-text-tertiary)' }}>Per Funded Loan</div>
              <div style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-sm)', marginTop: 'var(--lk-space-2)' }}>Paid when loan closes</div>
            </Surface>
            <Surface padding="var(--lk-space-4)" style={{ textAlign: 'center' }}>
              <Calendar size={40} color="var(--lk-primary-500)" style={{ marginBottom: 'var(--lk-space-2)' }} />
              <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>Monthly</div>
              <div style={{ color: 'var(--lk-text-tertiary)' }}>Payment Schedule</div>
              <div style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-sm)', marginTop: 'var(--lk-space-2)' }}>Paid on the 15th</div>
            </Surface>
            <Surface padding="var(--lk-space-4)" style={{ textAlign: 'center' }}>
              <Infinity size={40} color="var(--lk-primary-500)" style={{ marginBottom: 'var(--lk-space-2)' }} />
              <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>Unlimited</div>
              <div style={{ color: 'var(--lk-text-tertiary)' }}>Earning Potential</div>
              <div style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-sm)', marginTop: 'var(--lk-space-2)' }}>No cap on referrals</div>
            </Surface>
          </Grid>
        </Surface>
      </div>

      <FloatingOwenAssistant context="referral-earnings" />
      <FloatingKnowledge context="general" />
    </AppLayout>
  );
};

export default EarningsPage;
